/**
 * Ordering-mode resolution for the fan page.
 *
 * A stadium section's config is 'delivery' | 'pickup' | 'both'; an ORDER is
 * always two-valued — 'both' means the fan chooses at checkout, and what they
 * chose is what the order records (mirroring payment_mode 'fan_choice' vs
 * orders.payment_mode). These rules run in two places: TagPage builds the
 * submission from them, and the create-payment-intent edge function re-derives
 * them server-side against the section config so a tampered request can't
 * order pickup in a delivery-only section (Deno twin:
 * supabase/functions/_shared/ordering-mode.ts — keep equivalent).
 *
 * Kept out of TagPage so the precedence rules are testable without rendering
 * the fan page — same reasoning as orderNotice.ts and sponsorLogo.ts.
 */

import type { OrderingMode } from './supabase-types';

/** True only when the section lets the fan choose — the cart shows a chooser. */
export function canFanChoose(sectionMode: string | null | undefined): boolean {
  return sectionMode === 'both';
}

/**
 * Collapses section mode + fan choice into the two-valued mode an order
 * records. A single-mode section always wins over any fan choice; 'both'
 * follows the fan (delivery when they haven't chosen); a missing or unknown
 * section mode is delivery — the legacy convention everywhere else. Never
 * returns 'both'.
 */
export function resolveEffectiveOrderingMode(
  sectionMode: string | null | undefined,
  fanChoice: OrderingMode | null | undefined,
): OrderingMode {
  if (sectionMode === 'delivery' || sectionMode === 'pickup') return sectionMode;
  if (sectionMode === 'both') return fanChoice === 'pickup' ? 'pickup' : 'delivery';
  return 'delivery';
}

/**
 * The three mode fields of a create-payment-intent request. Collection point
 * and phone belong to pickup orders only; the phone is trimmed and blank
 * collapses to null.
 */
export function buildOrderModeFields(args: {
  sectionMode: string | null | undefined;
  fanChoice: OrderingMode | null | undefined;
  collectionPointId: string | null | undefined;
  fanPhone: string | null | undefined;
}): { ordering_mode: OrderingMode; collection_point_id: string | null; fan_phone: string | null } {
  const ordering_mode = resolveEffectiveOrderingMode(args.sectionMode, args.fanChoice);
  if (ordering_mode !== 'pickup') {
    return { ordering_mode, collection_point_id: null, fan_phone: null };
  }
  const phone = (args.fanPhone ?? '').trim();
  return {
    ordering_mode,
    collection_point_id: args.collectionPointId ?? null,
    fan_phone: phone || null,
  };
}

/** Menu title per section mode — 'both' gets the neutral wording. */
export function menuTitleKey(
  sectionMode: string | null | undefined,
): 'ordering.title' | 'ordering.titlePickup' | 'ordering.titleBoth' {
  if (sectionMode === 'pickup') return 'ordering.titlePickup';
  if (sectionMode === 'both') return 'ordering.titleBoth';
  return 'ordering.title';
}

/** An item the kitchen will not deliver to the seat. Undefined/null = deliverable (pre-migration rows). */
export function isPickupOnly(item: { seat_delivery_enabled?: boolean | null }): boolean {
  return item.seat_delivery_enabled === false;
}

/** Names of the cart's pick-up-only items, deduped, in menu order; [] when seat delivery is fine. */
export function pickupOnlyNames(
  cartItemIds: ReadonlyArray<string>,
  menuItems: ReadonlyArray<{ id: string; name: string; seat_delivery_enabled?: boolean | null }>,
): string[] {
  const cartIds = new Set(cartItemIds);
  return menuItems.filter((item) => cartIds.has(item.id) && isPickupOnly(item)).map((item) => item.name);
}
